import { useState } from "react";
import { Plus, Upload, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useEmailDomains,
  useCheckDomainDns,
  useDeleteEmailDomain,
  useEmailAuthSummary,
  useDmarcReports,
} from "@/hooks/use-email-auth";
import { DomainCard } from "@/components/email-auth/domain-card";
import { AddDomainDialog } from "@/components/email-auth/add-domain-dialog";
import { ReportUploadDialog } from "@/components/email-auth/report-upload-dialog";
import { ReportTable } from "@/components/email-auth/report-table";

export default function EmailAuthPage() {
  const { data: domainsData, isLoading: domainsLoading } = useEmailDomains();
  const { data: summaryData } = useEmailAuthSummary();
  const { data: reportsData } = useDmarcReports({ limit: 50 });
  const checkMutation = useCheckDomainDns();
  const deleteMutation = useDeleteEmailDomain();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [checkingId, setCheckingId] = useState<string | null>(null);

  const domains = domainsData?.domains ?? [];
  const reports = reportsData?.reports ?? [];

  async function handleCheck(id: string) {
    setCheckingId(id);
    try {
      await checkMutation.mutateAsync(id);
    } finally {
      setCheckingId(null);
    }
  }

  function handleDelete(id: string) {
    deleteMutation.mutate(id);
  }

  const score = summaryData?.posture_score ?? 0;
  const scoreColor = score >= 80 ? "text-green-600" : score >= 50 ? "text-yellow-600" : "text-red-600";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Email Authentication</h1>
          <p className="text-sm text-muted-foreground">
            Monitor SPF, DKIM, and DMARC for your email domains
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
            <Upload className="mr-1.5 h-4 w-4" />
            Upload Report
          </Button>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add Domain
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Posture Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${scoreColor}`}>{score}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Domains</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summaryData?.domain_count ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Aligned Messages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {summaryData?.dmarc_summary?.aligned_messages?.toLocaleString() ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Rejected/Quarantined</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              {(
                (summaryData?.dmarc_summary?.rejected_messages ?? 0) +
                (summaryData?.dmarc_summary?.quarantined_messages ?? 0)
              ).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Domain grid */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Monitored Domains</h2>
        {domainsLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-5 w-32 mb-2" />
                  <div className="flex gap-3">
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : domains.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Mail className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-3">No domains monitored yet</p>
              <Button onClick={() => setAddDialogOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" />
                Add Your First Domain
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {domains.map((d) => (
              <DomainCard
                key={d.id}
                domain={d}
                onCheck={handleCheck}
                onDelete={handleDelete}
                isChecking={checkingId === d.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* DMARC Reports */}
      <div>
        <h2 className="text-lg font-semibold mb-3">DMARC Reports</h2>
        <ReportTable reports={reports} />
      </div>

      {/* Dialogs */}
      <AddDomainDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
      <ReportUploadDialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen} />
    </div>
  );
}
